import React, { useState } from "react";
import { 
  Briefcase, Search, MapPin, Sparkles, Building2, Clock, GraduationCap, 
  Users, ChevronRight, CheckCircle2, ArrowRight, BookOpen, Filter, Wrench, Globe
} from "lucide-react";
import { getAllJobCategoryDefinitions, JobCategoryDefinition } from "../../utils/jobCategoryRegistry";
import { getAllSupportedStates, getAllQualifiedLocations } from "../../utils/locationRegistry";
import { getAllGuides } from "../../utils/guideRegistry";
import { SeoBreadcrumbs } from "./SeoBreadcrumbs";

interface JobDirectoryHubPageProps {
  onNavigate: (path: string) => void;
}

export function JobDirectoryHubPage({ onNavigate }: JobDirectoryHubPageProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const allCategories = getAllJobCategoryDefinitions();
  const allStates = getAllSupportedStates();
  const topLocations = getAllQualifiedLocations().slice(0, 24);
  const topGuides = getAllGuides().slice(0, 6);

  const mainCategories = allCategories.filter(c => c.type === "category");
  const employmentTypes = allCategories.filter(c => c.type === "employment-type");
  const studentTypes = allCategories.filter(c => c.type === "student");

  const filteredCategories = searchQuery.trim()
    ? allCategories.filter(c => 
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
        c.shortDescription.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.keySkills.some(s => s.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-12">
      {/* Breadcrumbs */}
      <SeoBreadcrumbs 
        items={[
          { label: "Home", path: "/" },
          { label: "Jobs Directory", path: "/jobs" }
        ]} 
        onNavigate={onNavigate}
      />

      {/* Hero Header */}
      <header className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-3xl p-8 sm:p-12 relative overflow-hidden shadow-xs space-y-6">
        <div className="relative z-10 max-w-3xl space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 dark:bg-blue-950/80 border border-blue-200 dark:border-blue-900/60 text-blue-700 dark:text-blue-300 text-xs font-bold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
            <span>Central Local Job Directory</span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-slate-950 dark:text-white leading-tight">
            Local Business Job Directory & Employment Hub
          </h1>

          <p className="text-slate-600 dark:text-slate-300 text-base sm:text-lg leading-relaxed">
            Discover local business hiring signals, entry-level positions, student opportunities, and job-type silos across independent businesses in your community.
          </p>

          {/* Quick Search */}
          <div className="pt-2">
            <div className="relative max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input 
                type="text"
                placeholder="Search job categories (e.g., retail, part-time, teen, warehouse)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-medium"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Search Filter Results */}
      {filteredCategories && (
        <section className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Filter className="w-5 h-5 text-blue-600" />
            <span>Matching Job Categories ({filteredCategories.length})</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCategories.map(cat => (
              <div 
                key={cat.slug}
                onClick={() => onNavigate(`/jobs/${cat.slug}`)}
                className="group p-5 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-blue-500 dark:hover:border-blue-500 shadow-sm hover:shadow-md transition-all cursor-pointer space-y-3"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/80 px-2.5 py-1 rounded-full">
                    {cat.type}
                  </span>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {cat.name}
                </h3>
                <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2">
                  {cat.shortDescription}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 1. Job Categories Grid */}
      {!filteredCategories && (
        <section className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                <Briefcase className="w-6 h-6 text-blue-600" />
                <span>Browse Jobs by Category</span>
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                Explore local employer roles by industry, specialty trade, and business sector.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {mainCategories.map(cat => (
              <div 
                key={cat.slug}
                onClick={() => onNavigate(`/jobs/${cat.slug}`)}
                className="group p-5 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-blue-500 dark:hover:border-blue-500 shadow-sm hover:shadow-md transition-all cursor-pointer space-y-3"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-bold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {cat.name}
                  </h3>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all" />
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2">
                  {cat.shortDescription}
                </p>
                <div className="pt-2 border-t border-slate-100 dark:border-slate-700/50 flex flex-wrap gap-1">
                  {cat.commonWorkplaces.slice(0, 2).map((w, i) => (
                    <span key={i} className="text-[10px] bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded">
                      {w}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 2. Employment Types & Work Schedules */}
      {!filteredCategories && (
        <section className="space-y-6">
          <div>
            <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
              <Clock className="w-6 h-6 text-emerald-600" />
              <span>Jobs by Employment Type & Schedule</span>
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              Filter roles by schedule flexibility, hour requirements, and experience expectations.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {employmentTypes.map(cat => (
              <div 
                key={cat.slug}
                onClick={() => onNavigate(`/jobs/${cat.slug}`)}
                className="group p-5 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-emerald-500 dark:hover:border-emerald-500 shadow-sm hover:shadow-md transition-all cursor-pointer space-y-3"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-bold text-slate-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                    {cat.name}
                  </h3>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all" />
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2">
                  {cat.shortDescription}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 3. Student & Teen Jobs */}
      {!filteredCategories && (
        <section className="space-y-6">
          <div>
            <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
              <GraduationCap className="w-6 h-6 text-purple-600" />
              <span>Jobs for Students & First-Time Workers</span>
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              Tailored opportunities for teenagers, high schoolers, college students, and first job seekers.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {studentTypes.map(cat => (
              <div 
                key={cat.slug}
                onClick={() => onNavigate(`/jobs/${cat.slug}`)}
                className="group p-5 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-purple-500 dark:hover:border-purple-500 shadow-sm hover:shadow-md transition-all cursor-pointer space-y-3"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-bold text-slate-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                    {cat.name}
                  </h3>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-purple-600 group-hover:translate-x-0.5 transition-all" />
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2">
                  {cat.shortDescription}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 4. Browse Jobs by Location */}
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
            <MapPin className="w-6 h-6 text-blue-600" />
            <span>Browse Jobs by Location</span>
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Explore local business hiring across top US state hubs and major cities.
          </p>
        </div>

        {/* Top Cities */}
        <div className="bg-slate-50 dark:bg-slate-800/50 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-4">
          <h3 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider">
            Popular Cities
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {topLocations.map(loc => (
              <button 
                key={loc.citySlug}
                onClick={() => onNavigate(`/jobs/${loc.citySlug}`)}
                className="text-left text-xs font-semibold text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 bg-white dark:bg-slate-800 p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-blue-400 transition-all truncate"
              >
                {loc.cityName}, {loc.stateAbbreviation}
              </button>
            ))}
          </div>
        </div>

        {/* States Hub */}
        <div className="bg-slate-50 dark:bg-slate-800/50 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-4">
          <h3 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider">
            Browse by US State
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {allStates.map(st => {
              const stateSlug = st.regionSlug || st.stateName.toLowerCase().replace(/\s+/g, "-");
              return (
                <button 
                  key={stateSlug}
                  onClick={() => onNavigate(`/${stateSlug}`)}
                  className="text-left text-xs font-semibold text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 bg-white dark:bg-slate-800 p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-blue-400 transition-all truncate"
                >
                  {st.stateName}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* 5. Job Search Guides */}
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-indigo-600" />
            <span>Job Search Strategy & Career Guides</span>
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Proven tactics for uncovering unadvertised roles, cold contacting businesses, and landing local jobs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {topGuides.map(guide => (
            <div 
              key={guide.slug}
              onClick={() => onNavigate(`/guides/${guide.slug}`)}
              className="group p-5 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-indigo-500 dark:hover:border-indigo-500 shadow-sm hover:shadow-md transition-all cursor-pointer space-y-3"
            >
              <h3 className="text-base font-bold text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                {guide.title}
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2">
                {guide.metaDescription}
              </p>
              <div className="inline-flex items-center gap-1 text-xs font-bold text-indigo-600 dark:text-indigo-400 group-hover:translate-x-1 transition-transform">
                <span>Read Strategy Guide</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Nearby Employer Search CTA */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-3xl p-8 sm:p-10 space-y-4 shadow-lg">
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight">
          Ready to Search Local Businesses Near You?
        </h2>
        <p className="text-blue-100 text-sm sm:text-base max-w-2xl">
          Use our interactive business discovery engine to filter nearby companies by category, hiring signals, and commute radius.
        </p>
        <button 
          onClick={() => onNavigate("/find-jobs")}
          className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white text-blue-900 font-bold text-sm hover:bg-blue-50 transition-colors shadow-md"
        >
          <span>Launch Local Business Search</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </section>
    </div>
  );
}
